import axios from 'axios';
import posthog from '../../posthog';

export class ApiError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const API_BASE_URL = import.meta.env.DEV
  ? '/api'
  : '/api';

/** Centralised API base URL — use this in raw fetch() calls instead of duplicating env logic. */
export const getApiBaseUrl = (): string => API_BASE_URL;

interface FetchOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  params?: Record<string, string | number | boolean>;
  [key: string]: any;
}

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const apiClient = async <T = any>(endpoint: string, options: FetchOptions = {}): Promise<T> => {
  const { params, headers, body, method = 'GET', ...customOptions } = options;

  const url = `${API_BASE_URL}${endpoint}`;
  const isFormData = body instanceof FormData;

  const defaultHeaders: Record<string, string> = isFormData
    ? {}
    : {
        'Content-Type': 'application/json',
      };

  const mergedHeaders = {
    ...defaultHeaders,
    ...headers,
  };

  if (isFormData && mergedHeaders['Content-Type']) {
    delete mergedHeaders['Content-Type'];
  }

  let data: any = undefined;
  if (body) {
    if (typeof body === 'string') {
      try {
        data = JSON.parse(body);
      } catch (e) {
        data = body;
      }
    } else {
      data = body;
    }
  }

  try {
    const response = await axios({
      url,
      method,
      headers: mergedHeaders,
      params,
      data,
      withCredentials: true,
      ...customOptions,
    });

    return response.data as T;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status || 0;
      const responseData = error.response?.data;
      const originalRequest = error.config as any;

      // Handle 401 specifically for refresh flow, avoid looping on /auth/refresh
      if (status === 401 && !originalRequest?.url?.includes('/auth/refresh') && !originalRequest?._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(async () => {
              const res = await axios(originalRequest!);
              return res.data as T;
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest!._retry = true;
        isRefreshing = true;

        try {
          // Attempt to refresh
          await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
          isRefreshing = false;
          processQueue(null, 'refreshed');
          
          // Retry original request
          return (await axios(originalRequest!)).data as T;
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(refreshError, null);
          
          posthog.reset();
          localStorage.removeItem('token');
          localStorage.removeItem('name');
          localStorage.removeItem('role');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }

      if (status === 401 && originalRequest?.url?.includes('/auth/refresh')) {
        // If the refresh itself fails, log them out.
        posthog.reset();
        localStorage.removeItem('token');
        localStorage.removeItem('name');
        localStorage.removeItem('role');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      const errorMessage = responseData?.detail || responseData?.message || error.message || 'An error occurred';
      throw new ApiError(errorMessage, status, responseData);
    }

    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(error.message || 'Connection failed. Please ensure the backend is running.', 0);
  }
};
